import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { usePosDayPlannedRequest, useUpdateIsOpenPosDayRequest, useUpdatePlannedPosDayRequest, useUpdatePlannedPosDaysRequest } from "../../hooks/useApi";
import FormControlLabel from '@mui/material/FormControlLabel';
import IOSSwitch from '../../components/IOSSwitch';
import BackIcon from '../../components/Icons/BackIcon';
import ForkIcon from '../../components/Icons/ForkIcon';
import RefreshIcon from '../../components/Icons/RefreshIcon';
import { NumericFormat } from 'react-number-format';
import { createIsActiveMap, createPlannedMap, countOpenDays, CURRENCY_MAP, MONTH_MAP, sumPlannedDays, currencyToNumber } from '../../utils/helpers';
import { FormatMoney } from '../../utils/number-formats';
import PlanStatus from '../../components/PlanStatus';
import debounce from 'lodash.debounce';
import TextInput from '../../components/TextInput';
import LoadingOverlay from '../../components/LoadingOverlay';
import './index.css';

const MonthlyTargetSales = (props) => {
  const posId = localStorage.getItem('posId');
  const posDs = localStorage.getItem('posDs');
  const yearNumber = localStorage.getItem('selectedYear');
  const monthNumber = localStorage.getItem('month');
  const period = localStorage.getItem('period');
  const weekNumber = localStorage.getItem('weekNumber');

  const [targetData, setTargetData] = useState(null);
  const [ s1, executeRequest ] = usePosDayPlannedRequest();
  const [ s2, updateOpenPosDay ] = useUpdateIsOpenPosDayRequest();
  const [ { loading: loadingDaysUpdate }, updatePlannedPosDays ] = useUpdatePlannedPosDaysRequest();
  const [ s4, updatePlannedPosDay ] = useUpdatePlannedPosDayRequest();
  const [checkboxStates, setCheckboxState] = useState([]);
  const [inputsStates, setInputsState] = useState([]);

  const changeSwitch = async (event) => {
    const { target: { value, checked } } = event;
    setCheckboxState((prevCheckStates) => {
      return { ...prevCheckStates, [value]: checked }
    });
    
    const bodyParams = { posId, month: monthNumber, year: yearNumber, period: period, dayOfPeriod: value, isOpen: checked };
    await updateOpenPosDay({ bodyParams });
    await FetchPosMonthTarget();
  }

  const debouncedSave = useCallback(
    debounce(async(event, nextValue) => {
      const { target: { value, name } } = event;
      const bodyParams = { posId, month: monthNumber, year: yearNumber, dayOfPeriod: name, planned: currencyToNumber(value) || 0, period: period };
      await updatePlannedPosDay({ bodyParams });
      await FetchPosMonthTarget();
    }, 1000),
    [], // will be created only once initially
  );

  const handleInputChange = async (event) => {
    const { target: { value, id, name }, key } = event;
    setInputsState((prevInputsStates) => {
      return { ...prevInputsStates, [name]: currencyToNumber(value) }
    });
    debouncedSave(event, {[name]: currencyToNumber(value)});
  }

  const FetchPosMonthTarget = useCallback(async () => {
    if (posId && monthNumber && yearNumber && period)
      {
        const bodyParams = {
          posId,
          month: monthNumber,
          year: yearNumber,
          period
        };

        const response = await executeRequest({ bodyParams });
        setTargetData(response?.data);
        setCheckboxState(createIsActiveMap(response));
        setInputsState(createPlannedMap(response));
      }
  }, [posId, monthNumber, yearNumber])

  useEffect(() => {
    FetchPosMonthTarget();
  }, [posId, monthNumber, yearNumber]);

  const handleSplitBudget = useCallback(async () => {
    const monthValue = Math.floor(budget / countOpenDays(targetData));
    const daysValues = targetData?.posDays.filter((pd) => pd?.isOpen && pd?.isEditable).map(({ dayNumber}) => {
      return {
        dayOfPeriod: dayNumber,
        planned: monthValue
      }
    });
    const bodyParams = { posId, period, month: monthNumber, year: yearNumber, days: daysValues };
    await updatePlannedPosDays({ bodyParams });
    await FetchPosMonthTarget();
    return false;
  }, [targetData]);

  const handleResetBudget = useCallback(async () => {
    const daysValues = targetData?.posDays.map(({ dayNumber}) => {
      return {
        dayOfPeriod: dayNumber,
        planned: 0
      }
    });
    const bodyParams = { posId, period, month: monthNumber, year: yearNumber, days: daysValues };
    await updatePlannedPosDays({ bodyParams });
    await FetchPosMonthTarget();
    return false;
  }, [targetData]);

  const handleGoBackClick = () => {
    const backUrl = localStorage.getItem('backUrl');
    window.location.replace(backUrl);
  }

  const handleTurnoverSaClick = () => {
    window.location.replace('/sa-kpi');
  }

  if (loadingDaysUpdate)
    return <LoadingOverlay />

  const budget = targetData?.budget;
  const plannedBudget = sumPlannedDays(targetData);
  const remaining = plannedBudget - budget;
  const splitIsEnabled = targetData?.posDays[0]?.isEditable;
  const currency = CURRENCY_MAP[targetData?.currencyId] ? CURRENCY_MAP[targetData?.currencyId] : targetData?.currencyId;
  return (
    <>
      <div className="etroContainer titleWrapper dFlex">
        <div className="dFlex justifyContentStart alignItemsCenter gap-1 left">
          <Link onClick={handleGoBackClick} ><BackIcon /></Link>
          <h1>{targetData?.posDs} / {MONTH_MAP[monthNumber]} {yearNumber} / Week {weekNumber}</h1>
        </div>
        <div className="center">Weekly budget</div>
        <div className="right">
          {/* <OutlinedBtn title="View Turnover per SA" variant="outlined" onClick={handleTurnoverSaClick} to="/sa-kpi" >View Turnover per SA</OutlinedBtn> */}
        </div>
      </div>
      
      { targetData && (
        <>
          <div className="tableWrapper">
            <div className="budgetWrapper gap-3">
              <div className="budgetItem">
                <div className="title">Budget:</div>
                <div className="value">{<NumericFormat value={budget} thousandSeparator=" " prefix={`${currency} `} displayType="text" decimalScale={0} />}</div> 
              </div>
              <div className="budgetItem dFlex justifyContentSpaceBetween">
                <div>
                  <div className="title">Planned:</div> 
                  <div className="value">{<NumericFormat value={plannedBudget} thousandSeparator=" " prefix={`${currency} `} displayType="text" decimalScale={0} />}</div> 
                </div>
                <PlanStatus value={remaining} />
              </div>
              <div className="budgetItem">
                <div className="title">Remaining:</div> 
                <div className="value">{<NumericFormat className={remaining < 0 ? 'underTarget' : ''} value={remaining} thousandSeparator=" " prefix={`${currency} `} displayType="text" decimalScale={0} />}</div> 
              </div>
            </div>
          </div>
          <div className="weeklyBudgetTableWrapper">
            <table className="monthlySalesTable" style={{width: "100%", marginTop: '34px', tableLayout: 'fixed'}}>
              <thead>
                <tr className="bgTableGrey">
                  <th className="short borderLeftNone">Status</th>
                  <th>Day</th>
                  <th className="" style={{maxWidth: '500px', minWidth: '380px'}}>
                    <div style={{display: "flex", "justifyContent": "space-between"}}>
                      <div>Planned</div>
                      <div className="tabWrapper">
                        <a disabled={true} className={splitIsEnabled ? "tabLink splitBudget" : "tabLink"} onClick={splitIsEnabled ? handleSplitBudget : null} ><ForkIcon /> Split Budget</a>
                        &nbsp;&nbsp;&nbsp;
                        <a className="tabLink resetAll" onClick={splitIsEnabled ? handleResetBudget : null} ><RefreshIcon /> Reset All</a>
                      </div>
                    </div>
                  </th>
                  <th className="">Actual Sales</th>
                  <th className="">&#916; Sales</th>
                  <th className="borderRightNone">&#916; %</th>
                </tr>
              </thead>
              <tbody>
              {
                targetData.posDays.map(({ dayNumber, dayName, planned, actualSale, deltaSale, deltaPercentageSale, isOpen, isBold, isEditable})=>{
                  return (
                    <tr className={isOpen ? '' : 'bgTableGrey'}>
                      <td className="borderLeftNone">
                        <FormControlLabel
                          control={<IOSSwitch sx={{ m: 1 }} onChange={changeSwitch} value={dayNumber} checked={checkboxStates[dayNumber]} />}
                          label=""
                        />
                      </td>
                      <td className={(isBold && isOpen) ? 'isBold' : ''} >{dayNumber} {dayName}</td>
                      <td style={{maxWidth: '500px', minWidth: '380px'}}>
                        {
                          (isOpen && isEditable) && (
                            <FormatMoney
                              value={inputsStates[dayNumber]}
                              prefix={currency}
                              renderText={(value) =>
                                <TextInput
                                  value={value}
                                  name={dayNumber}
                                  onChange={handleInputChange}
                                />
                              }
                            />
                          )
                        }
                        {
                          (isOpen && !isEditable) && (
                            <FormatMoney value={inputsStates[dayNumber]} prefix={currency} />
                          )
                        }
                        {
                          !isOpen && ('N/A')
                        }
                      </td>
                      <td>
                        {isOpen && (<FormatMoney value={actualSale} prefix={currency} />)}
                        {!isOpen && ('N/A')}
                        </td>
                      <td>
                        {isOpen && (<FormatMoney value={deltaSale} prefix={currency} />)}
                        {!isOpen && ('N/A')}
                      </td>
                      <td className="borderRightNone">
                        {isOpen && (<NumericFormat decimalScale={0} displayType="text" value={deltaPercentageSale} suffix=" %" />)}
                        {!isOpen && ('N/A')}
                      </td>
                    </tr>
                  );              
                })
              }
              <tr className="bgTableGrey footer totalsRow">
                <td className="borderLeftNone" colSpan="3">Total</td>
                <td className="borderRightNone textCenter" colSpan="3"><NumericFormat value={plannedBudget} thousandSeparator=" " prefix={`${currency} `} displayType="text" decimalScale={0} /></td>
              </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </>  
  );
};

export default MonthlyTargetSales;
